import { create } from 'zustand';

import { getRfqConfigurationsQuery } from 'Services/Query/Auto-RFQ';

import { getQuoteConfigurationsQuery } from 'Services';
import { ConfigurationProps } from '../interfaces';

export enum PricingMode {
  NO_PRICING = 'NO_PRICING',
  COST_ONLY = 'COST_ONLY',
  CUSTOMER_PRICING = 'CUSTOMER_PRICING'
}

interface ConfigState extends ConfigurationProps {
  isLoading: boolean;
  isInitialized: boolean;
  error: string | null;
}

interface ConfigStore extends ConfigState {
  fetchConfigurations: () => Promise<void>;
  updateConfiguration: (config: Partial<ConfigurationProps>) => void;
  reset: () => void;
}

const initialState: ConfigState = {
  _id: '',
  rfqListFields: [],
  validUntil: undefined,
  enableCRMIntegration: false,
  enableOrderHistory: false,
  enableApprovalProcess: false,
  enableOpportunity: false,
  enableShipTo: false,
  marginPercentage: 0,
  enableSpecialPurchaseRequest: false,
  enableEquipmentRequest: false,
  enableValidityDate: false,
  enableNewProspect: false,
  fullEditableQuotes: false,
  nonEditableSellTypes: [],
  pricing: {
    mode: PricingMode.NO_PRICING,
    defaultMarginEnabled: false,
    defaultMarginPercent: 0
  },
  rfqToQuoteEnabled: false,
  enableAccountSelection: false,
  enableProspect: false,
  requireSegmentForProspect: false,
  defaultSegment: null,
  showManufacturerDetails: false,
  isLoading: false,
  isInitialized: false,
  error: null
};

// RQF Quote configurations
export const useRfqConfigStore = create<ConfigStore>((set, get) => ({
  ...initialState,

  fetchConfigurations: async () => {
    if (get().isInitialized) return;

    set({ isLoading: true, error: null });

    try {
      const query = getRfqConfigurationsQuery();
      const response = await query.queryFn();

      if (response) {
        set({
          ...response,
          isLoading: false,
          isInitialized: true,
          error: null
        });
      }
    } catch (error) {
      set({
        isLoading: false,
        error:
          error instanceof Error
            ? error.message
            : 'Failed to fetch configurations'
      });
    }
  },

  updateConfiguration: (config: Partial<ConfigurationProps>) =>
    set(state => ({ ...state, ...config })),

  reset: () => set({ ...initialState })
}));

// Quote configurations
export const useQuoteConfigStore = create<ConfigStore>((set, get) => ({
  ...initialState,

  fetchConfigurations: async () => {
    if (get().isInitialized) return;

    set({ isLoading: true, error: null });

    try {
      const query = getQuoteConfigurationsQuery();
      const response = await query.queryFn();

      if (response) {
        set({
          ...response,
          isLoading: false,
          isInitialized: true,
          error: null
        });
      }
    } catch (error) {
      set({
        isLoading: false,
        error:
          error instanceof Error
            ? error.message
            : 'Failed to fetch configurations'
      });
    }
  },

  updateConfiguration: (config: Partial<ConfigurationProps>) =>
    set(state => ({ ...state, ...config })),

  reset: () => set({ ...initialState })
}));
